import { createContext, useContext, useState, useCallback } from 'react';
import { Box } from '@mui/material';

// Context for managing tree state
const TreeContext = createContext({
  expanded: [],
  setExpanded: () => {},
  defaultCollapseIcon: null,
  defaultExpandIcon: null,
  onNodeToggle: () => {},
});

export const useTreeContext = () => {
  const context = useContext(TreeContext);
  if (!context) {
    throw new Error('useTreeContext must be used within a TreeView');
  }
  return context;
};

const TreeView = ({
  children,
  'aria-label': ariaLabel,
  defaultCollapseIcon,
  defaultExpandIcon,
  sx,
  'data-test': dataTest,
  defaultExpanded = [],
  onNodeToggle,
  ...props
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleNodeToggle = useCallback(
    (nodeId) => {
      setExpanded((prev) => {
        const newExpanded = prev.includes(nodeId)
          ? prev.filter((id) => id !== nodeId)
          : [...prev, nodeId];

        if (onNodeToggle) {
          onNodeToggle(null, newExpanded);
        }

        return newExpanded;
      });
    },
    [onNodeToggle],
  );

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const contextValue = {
    expanded,
    setExpanded,
    defaultCollapseIcon,
    defaultExpandIcon,
    onNodeToggle: handleNodeToggle,
  };

  return (
    <TreeContext.Provider value={contextValue}>
      <Box
        component='div'
        aria-label={ariaLabel}
        data-test={dataTest}
        sx={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          ...sx,
        }}
        {...props}
      >
        {children}
      </Box>
    </TreeContext.Provider>
  );
};

export default TreeView;
